import React, { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { DOCUMENT_TYPE } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { IndexPageActions } from 'src/components/PageLayout/IndexPage/IndexPageActions'
import { ModalFormId } from 'src/components/ModalForms'
import {
  DropdownButtonItems,
  DropdownPrimaryButton,
} from 'src/components/Reusable/Buttons/DropdownPrimaryButton/DropdownPrimaryButton'
import { useModalState } from 'src/Modal/ModalStateContext'
import { useAccountReceptors } from 'src/components/async/model/receptor/useReceptors'
import { HUBSPOT } from 'src/Utils/hubspot/hubspot.utils'
import { getReceptorTemplate } from 'src/xgenerated'
import { mergeReceptorData } from 'src/pages/Receptors/receptors.helpers'
import { FlexRow, FlexAlign } from 'src/components/Reusable/Flex'
import { FilterBar } from 'src/components/Reusable/IndexPage/FilterBar'
import { FilterBarConfigs } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.constants'
import { FilterParam } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'
import { INDEX_PAGE_WITH_FILTERS } from 'src/components/Reusable/IndexPage/IndexPage.constants'
import {
  Tooltip,
  TooltipPositions,
  TOOLTIP_TRIGGERS,
} from 'src/components/Reusable/Tooltip/Tooltip'
import { useGooglePicker } from 'src/lib/Linkers/modals/Google Drive Wizard/GoogleDriveWizard.hooks'
import { GOOGLE_PICKER_SUPPORTED_FILE_TYPES } from 'src/lib/Linkers/modals/Google Drive Wizard/GoogleDriveWizard.constants'
import { useThrobberContext } from 'src/Throbber'
import palette from 'src/designSystem/variables/palette'
import { useSharepointFilePickerContext } from 'src/lib/Linkers/modals/SharePoint Picker/SharepointFilePickerContext'
import { useConfluenceFilePickerContext } from 'src/lib/Linkers/modals/Confluence Picker/ConfluenceFilePickerContext'
import {
  IndexPageContainer,
  IndexPageContent,
  IndexPageHeader,
} from 'src/components/PageLayout/PageLayout.styles'

import { useAutomatedScanContext } from 'src/pages/Receptors/context/AutomatedScanContext'
import { DocumentScanGrid } from '../grid/DocumentScanGrid'
import {
  DocumentReceptor,
  receptorMap,
  ReceptorScanType,
} from '../../Evidence/evidence.constants'
import { InfoIcon } from '../../Evidence/evidence.styles'
import { ReceptorStatusLabels } from '../receptors.constants'

export const DocumentScanIndexPageActions = (): JSX.Element => {
  const { openModal } = useModalState()
  const {
    setInitialFolderScanReceptorId,
    setSelectedScan,
    setInitialReceptorModelId,
    setDocumentType,
  } = useAutomatedScanContext()
  const { data: receptorData } = useAccountReceptors()
  const openPicker = useGooglePicker()
  const { startThrobber, stopThrobber } = useThrobberContext()
  const { setReceptor: setSharepointReceptor } =
    useSharepointFilePickerContext()
  const { setReceptor: setConfluenceReceptor } =
    useConfluenceFilePickerContext()

  const getFolderScanOptions = (): DropdownButtonItems[] => {
    if (!receptorData) return []

    const providers = [
      DocumentReceptor.SharePoint,
      DocumentReceptor.GoogleDrive,
      DocumentReceptor.Confluence,
    ]

    return providers.flatMap((provider): DropdownButtonItems[] => {
      const modelId = receptorMap[provider]
      const receptors = receptorData
        .getReceptorsList()
        .filter((r) => r.getModelid() === modelId && r.getIsenabled())

      if (receptors.length > 0) {
        return receptors.map((receptor) => {
          const template = getReceptorTemplate(receptor.getModelid())
          const baseReceptorName = template ? template.name : provider
          const tenantId = receptor.getTenantid()
          const receptorName =
            receptors.length > 1 && tenantId
              ? `${baseReceptorName} (${tenantId})`
              : baseReceptorName

          return {
            name: `Add ${receptorName} folder scan`,
            onClick: async () => {
              setDocumentType(DOCUMENT_TYPE.EVIDENCE)
              setInitialFolderScanReceptorId(receptor.getId())
              if (provider === DocumentReceptor.GoogleDrive) {
                try {
                  startThrobber()
                  const config = {
                    allowMultiselect: true,
                    viewId: google.picker.ViewId.DOCS,
                    selectFolderEnabled: true,
                    modalId: ModalFormId.ADD_FOLDER_SCAN,
                    mimeTypes: GOOGLE_PICKER_SUPPORTED_FILE_TYPES,
                    selectableMimeTypes: 'application/vnd.google-apps.folder',
                  }
                  const activeReceptor =
                    template && mergeReceptorData(receptor, template)
                  if (activeReceptor) {
                    await openPicker(config, activeReceptor)
                  }
                } finally {
                  stopThrobber()
                }
              } else if (provider === DocumentReceptor.SharePoint) {
                setSharepointReceptor(receptor)
                openModal(ModalFormId.SHAREPOINT_FOLDER_PICKER)
              } else if (provider === DocumentReceptor.Confluence) {
                setConfluenceReceptor(receptor)
                openModal(ModalFormId.CONFLUENCE_FOLDER_PICKER)
              } else {
                openModal(ModalFormId.ADD_FOLDER_SCAN)
              }
            },
          }
        })
      }

      return [
        {
          name: (
            <Tooltip
              id={`disabled-receptor-${provider}`}
              tooltipBody={
                <span>
                  {provider} is not configured yet.{' '}
                  <span
                    style={{
                      color: palette.blue[100],
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      setDocumentType(DOCUMENT_TYPE.EVIDENCE)
                      setInitialFolderScanReceptorId(undefined)
                      setSelectedScan(
                        ReceptorScanType.DOCUMENT_MANAGEMENT_RECEPTOR,
                      )
                      setInitialReceptorModelId(receptorMap[provider])
                      openModal(ModalFormId.ADD_RECEPTOR_SCAN)
                    }}
                  >
                    Setup credentials
                  </span>
                </span>
              }
              trigger={[TOOLTIP_TRIGGERS.HOVER, TOOLTIP_TRIGGERS.FOCUS]}
              placement={TooltipPositions.left}
              keepOpenOnHover
            >
              <FlexRow
                align={FlexAlign.CENTER}
                justify={FlexAlign.SPACE_BETWEEN}
                isFullWidth
                gap={8}
              >
                <span>Add {provider} folder scan</span>
                <InfoIcon />
              </FlexRow>
            </Tooltip>
          ),
          onClick: (e) => e.preventDefault(),
          disabled: true,
          className: 'custom-disabled',
        },
      ]
    })
  }

  const options: DropdownButtonItems[] = [
    {
      name: 'Add receptor scan',
      onClick: () => {
        setInitialFolderScanReceptorId(undefined) // Reset specific receptor selection
        openModal(ModalFormId.ADD_RECEPTOR_SCAN)
      },
    },
    ...getFolderScanOptions(),
  ]
  return (
    <IndexPageActions
      description="Add a receptor or folder to automatically scan for relevant compliance evidence."
      learnMoreUrl={HUBSPOT.SCHEDULING}
      buttonGroup={
        <DropdownPrimaryButton label={options[0].name} items={options} />
      }
    />
  )
}

export const DocumentScanTabContent = (): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams()
  const didInitStatusFilter = useRef(false)

  useEffect(() => {
    if (didInitStatusFilter.current) return
    didInitStatusFilter.current = true

    const statusFilters = searchParams.getAll(FilterParam.STATUS)
    if (statusFilters.length > 0) return

    const nextParams = new URLSearchParams(searchParams)
    nextParams.set(FilterParam.STATUS, ReceptorStatusLabels.ACTIVE)
    setSearchParams(nextParams, { replace: true })
  }, [searchParams, setSearchParams])

  return (
    <IndexPageContainer>
      <IndexPageHeader>
        <DocumentScanIndexPageActions />
        <FilterBar
          config={FilterBarConfigs[INDEX_PAGE_WITH_FILTERS.DOCUMENT_SCAN]}
          searchBarOptions={{ withContentSearch: false }}
        />
      </IndexPageHeader>
      <IndexPageContent>
        <DocumentScanGrid documentTypeFilter={DOCUMENT_TYPE.EVIDENCE} />
      </IndexPageContent>
    </IndexPageContainer>
  )
}
